import { cloneDeep } from 'lodash';

import { TradeItem } from '../../contexts/trade';
import { GetAsset } from '../game-document/assets';
import { GameDocument } from '../../types/game-document';
import { GetResourceValue } from '../game-document/resources';
import { GetPreGameSetting } from '../game-document/settings';
import { Item } from '../../types/game-document/entities/item';
import { PlayerState, PlayerStateFull, TeamStateFull } from '../../types/state';
import { ItemAchievement } from '../../features/in-game/overlay/drawer/tabs/inventory';

/**
 * Set visibility of inventory
 * @param gameDocument - The Game document
 * @param gamePlayer - The GamePlayer state
 */
export const ShowInventory = (
  gamePlayer: PlayerState,
  gameDocument: GameDocument
) => {
  if (gamePlayer?.status === 'playing') {
    const GetPreGameInventory = GetPreGameSetting(gameDocument);
    return GetPreGameInventory.showPreGameInventory;
  }
  return true;
};

/**
 * Loads the Item's resources and combines the result.
 * @param gameDocument
 * @param language
 * @param itemId
 * @param quantity
 */
export const GetItemAchievement = (
  gameDocument: GameDocument,
  language: string,
  itemId: string,
  quantity: number
) => {
  let inventoryItem = {
    ...GetAsset<Item>(gameDocument.assets.items!, itemId),
    quantity
  } as ItemAchievement;
  inventoryItem.titleRes = GetResourceValue(
    gameDocument,
    inventoryItem.titleResId!,
    language
  );
  inventoryItem.imageRes = GetResourceValue(
    gameDocument,
    inventoryItem.imageResId!,
    language
  );
  inventoryItem.summaryRes = GetResourceValue(
    gameDocument,
    inventoryItem.summaryResId!,
    language
  );
  return inventoryItem;
};

/**
 * Converts the Player's Inventory Items into InventoryItemAchievements
 * @param gameDocument
 * @param playerState
 */
export const CastPlayerInventory = (
  gameDocument: GameDocument,
  playerState: PlayerStateFull
) =>
  playerState.inventory!.map((item, index) =>
    GetItemAchievement(
      gameDocument,
      playerState.language?.name! ?? '',
      item.id,
      item.quantity
    )
  ) ?? [];

/**
 * Converts the Team's Inventory Items into InventoryItemAchievements
 * @param gameDocument
 * @param teamState
 */
export const CastTeamInventory = (
  gameDocument: GameDocument,
  teamState: TeamStateFull
) =>
  (teamState.inventory ?? []).map((item, index) =>
    GetItemAchievement(gameDocument, '', item.id, item.quantity)
  ) ?? [];

/**
 * Search the list of InventoryItemAchievements for the provided search term.
 * @param items
 * @param searchTerm
 */
export const SearchItemAchievement = (
  items: ItemAchievement[],
  searchTerm: string
) =>
  items.filter((i) =>
    i.titleRes.toLowerCase().includes(searchTerm.toLowerCase())
  );

export const AcceptTradeItems = (
  playerState: PlayerStateFull,
  givenItems: TradeItem[],
  receivedItems: TradeItem[]
) => {
  const myItems = cloneDeep(playerState.inventory);

  if (myItems) {
    // First handle received items
    const updatedItems = [...myItems];

    // Process received items
    receivedItems.forEach((receivedItem) => {
      const existingItem = updatedItems.find(
        (item) => item.id === receivedItem.itemId
      );
      if (existingItem) {
        // Increase quantity if item exists
        existingItem.quantity += receivedItem.quantity;
      } else {
        // Add new item if it doesn't exist
        updatedItems.push({
          id: receivedItem.itemId,
          quantity: receivedItem.quantity
        });
      }
    });

    // Process given items (reduce quantities)
    const result = updatedItems
      .map((item) => {
        const givenItem = givenItems.find((i) => i.itemId === item.id);
        if (givenItem) {
          item.quantity = item.quantity - givenItem.quantity;
        }
        return item;
      })
      .filter((item) => item.quantity > 0);

    return {
      ...playerState,
      inventory: result
    };
  }

  return playerState;
};
